import { AfterViewInit, ChangeDetectorRef, Component, inject, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslocoLocaleService } from '@ngneat/transloco-locale';
import { DetailsTableRow } from '@tcc-mono/shared/ui/details-table';
import { AreYouSureService } from '@tcc-mono/shared/ui/services';
import { CalcAgePipe } from '@tcc-mono/shared/utils';
import { Animal, getSelectionMarkColorHex, SelectionMarkColor } from '@tcc-mono/vandijk/shared/data-access/models';
import { AnimalSelectionRepository } from '@tcc-mono/vandijk/shared/data-access/state';
import { AnimalLookupDialogService } from '@tcc-mono/vandijk/shared/features/animal-lookup-dialog';
import { filter, take } from 'rxjs';
import { DialogButton } from '@tcc-mono/shared/ui/dialog-layout';

@Component({
  templateUrl: './animal-selection-animal-details-dialog.component.html',
  styleUrls: ['./animal-selection-animal-details-dialog.component.scss']
})
export class AnimalSelectionAnimalDetailsDialogComponent implements AfterViewInit, OnInit {
  private readonly _locale: TranslocoLocaleService = inject(TranslocoLocaleService);
  private readonly _agePipe: CalcAgePipe = inject(CalcAgePipe);
  private readonly _modalCtrl: ModalController = inject(ModalController);
  private readonly _areYouSure: AreYouSureService = inject(AreYouSureService);
  private readonly _repo: AnimalSelectionRepository = inject(AnimalSelectionRepository);
  private readonly _cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly _animalLookupDialog = inject(AnimalLookupDialogService);

  @Input() public animal: Animal;
  @Input() public selection: { id: number };
  /** Alleen kleuren die voor deze selectie aanstaan (voor snel klikken). */
  public usedColors: SelectionMarkColor[] = [];
  public rows: DetailsTableRow[] = [];
  public deleteButtons: DialogButton[] = [
    {
      label: 'Verwijderen',
      color: 'danger',
      func: () => this.removeAnimal()
    }
  ];

  @ViewChild('weightTmpl') public weightTmpl: TemplateRef<any>;
  @ViewChild('genderTmpl') public genderTmpl: TemplateRef<any>;
  @ViewChild('colorTmpl') public colorTmpl: TemplateRef<any>;
  @ViewChild('healthyTmpl') public healthyTmpl: TemplateRef<any>;
  @ViewChild('levensnummerTmpl') public levensnummerTmpl: TemplateRef<any>;

  public ngOnInit(): void {
    if (this.selection?.id) {
      this._repo.getSelectionMarkColorsForSelection(this.selection.id).pipe(take(1)).subscribe(colors => {
        this.usedColors = (colors || []).filter(c => c.used !== false);
      });
    }
  }

  public ngAfterViewInit(): void {
    this.rows = this._buildRows();
    this._cdr.detectChanges();
  }

  private _buildRows(): DetailsTableRow[] {
    return [
      {
        key: 'Levensnummer',
        value: this.animal,
        template: this.levensnummerTmpl
      },
      {
        key: 'Werknummer',
        value: this.animal.work_number,
      },
      {
        key: 'Geboortedatum',
        value: this.animal.date_of_birth,
        valueType: 'date'
      },
      {
        key: 'Leeftijd',
        value: `${this._a(this.animal.date_of_birth)} dgn.`
      },
      {
        key: 'Gewicht',
        value: this.animal,
        template: this.weightTmpl
      },
      {
        key: 'Prijs',
        value: `€ ${this._c(this.animal.price_ex_vat || 0)}`
      },
      {
        key: 'Geslacht',
        template: this.genderTmpl,
        value: this.animal
      },
      {
        key: 'Kleur',
        template: this.colorTmpl,
        value: this.animal
      },
      {
        key: 'Kleur / Markering',
        value: this.animal.selection_mark_color_name ?? '—'
      },
      {
        key: 'Leverancier',
        value: this.animal?.ride_relation
      },
      {
        key: 'Gezond',
        template: this.healthyTmpl,
        value: this.animal
      },
      {
        key: 'Reden ongezond',
        value: this.animal.not_healthy_reason_name
      },
      {
        key: 'Opmerkingen',
        value: this.animal.remarks
      }
    ];
  }

  public close(): void {
    this._modalCtrl.dismiss();
  }

  public openAnimalInfo(): void {
    if (!this.animal?.country_code || !this.animal?.identification_number) {
      return;
    }

    this._animalLookupDialog
      .openInfo({
        country_code: this.animal.country_code,
        identification_number: this.animal.identification_number
      })
      .pipe(take(1))
      .subscribe();
  }

  public removeAnimal(): void {
    this._areYouSure.show({
      message: 'Het dier wordt van de huidige selectie verwijderd.'
    })
      .pipe(
        filter(res => !!res),
        take(1)
      )
      .subscribe(() => this._modalCtrl.dismiss(null, 'cancel'));
  }

  public getMarkColorHex(value: string): string {
    return getSelectionMarkColorHex(value);
  }

  public onMarkColorChange(selectionMarkColor: string | null): void {
    if (!this.selection?.id || !this.animal?.id) return;
    this._repo.updateAnimalMarkColor(this.selection.id, this.animal.id, selectionMarkColor)
      .pipe(take(1))
      .subscribe({
        next: () => this._modalCtrl.dismiss(null, 'updated')
      });
  }

  private _c(price: number): string {
    return this._locale.localizeNumber(price,
      'decimal',
      this._locale.getLocale(),
      {
        maximumFractionDigits: 2
      });
  }

  private _a(date: string, base: number = 1): number {
    return this._agePipe.transform(date, { base: base });
  }
}
